import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Users, Loader2, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useUserSession } from "@/hooks/useUserSession";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CreateTenantForm } from "./CreateTenantForm";
import { JoinTenantForm } from "./JoinTenantForm";
import { PendingApproval } from "./PendingApproval";

type OnboardingStep = "choice" | "create" | "join" | "waiting";

interface OnboardingStatus {
  status: string;
  currentStep: string | null;
  hasTenants: boolean;
  pendingRequest: {
    tenantId: string;
    tenantName: string;
    status: string;
    createdAt: string;
  } | null;
}

/**
 * Main onboarding wizard page.
 *
 * Shown to users who have signed up but have no tenant membership.
 * Guides them through either:
 * 1. Creating a new tenant (becoming admin)
 * 2. Joining an existing tenant (via join code)
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { refetch: refetchSession } = useUserSession();
  const [step, setStep] = useState<OnboardingStep>("choice");
  const [isLoading, setIsLoading] = useState(true);
  const [pendingRequest, setPendingRequest] = useState<OnboardingStatus["pendingRequest"]>(null);

  // Fetch current onboarding status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await apiFetch.get<OnboardingStatus>("/api/onboarding/status");

        // If user already has tenants, redirect to dashboard
        if (data.hasTenants) {
          navigate("/", { replace: true });
          return;
        }

        // Resume from last step or pending request
        if (data.pendingRequest) {
          setPendingRequest(data.pendingRequest);
          setStep("waiting");
        } else if (data.currentStep) {
          setStep(data.currentStep as OnboardingStep);
        }
      } catch (err) {
        // Redirect to login if not authenticated
        if (err instanceof ApiError && err.isUnauthorized()) {
          navigate("/login", { replace: true });
          return;
        }
        // Ignore other errors, default to choice step
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [navigate]);

  // Track step changes
  const updateProgress = async (newStep: OnboardingStep) => {
    setStep(newStep);
    try {
      await apiFetch.post("/api/onboarding/progress", { step: newStep });
    } catch {
      // Ignore errors
    }
  };

  const handleCreateSuccess = () => {
    // Tenant created successfully - refetch session to get new tenant/permissions
    // then redirect to dashboard
    refetchSession();
    navigate("/", { replace: true });
  };

  const handleJoinSuccess = (request: OnboardingStatus["pendingRequest"]) => {
    setPendingRequest(request);
    setStep("waiting");
  };

  const handleCancelRequest = async () => {
    try {
      await apiFetch.post("/api/onboarding/cancel-request");
      setPendingRequest(null);
      setStep("choice");
    } catch {
      // Handle error
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        {/* Back button for create/join steps */}
        {(step === "create" || step === "join") && (
          <Button
            variant="ghost"
            size="sm"
            className="mb-4"
            onClick={() => updateProgress("choice")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        )}

        {step === "choice" && (
          <ChoiceStep
            onCreateClick={() => updateProgress("create")}
            onJoinClick={() => updateProgress("join")}
          />
        )}

        {step === "create" && (
          <CreateTenantForm onSuccess={handleCreateSuccess} />
        )}

        {step === "join" && (
          <JoinTenantForm onSuccess={handleJoinSuccess} />
        )}

        {step === "waiting" && pendingRequest && (
          <PendingApproval
            tenantName={pendingRequest.tenantName}
            createdAt={new Date(pendingRequest.createdAt)}
            onCancel={handleCancelRequest}
          />
        )}
      </div>
    </div>
  );
}

interface ChoiceStepProps {
  onCreateClick: () => void;
  onJoinClick: () => void;
}

function ChoiceStep({ onCreateClick, onJoinClick }: ChoiceStepProps) {
  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Bienvenido a Folio</CardTitle>
        <CardDescription>
          Para comenzar, elige como quieres configurar tu cuenta
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <button
          onClick={onCreateClick}
          className="w-full p-6 rounded-lg border-2 border-border hover:border-primary hover:bg-accent transition-colors text-left"
        >
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Crear una empresa</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Inicia tu propia empresa y conviertete en administrador
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={onJoinClick}
          className="w-full p-6 rounded-lg border-2 border-border hover:border-primary hover:bg-accent transition-colors text-left"
        >
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Unirme a una empresa</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Ingresa el codigo de invitacion para unirte a una empresa existente
              </p>
            </div>
          </div>
        </button>
      </CardContent>
    </Card>
  );
}
